import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, Select, MenuItem, Box, Snackbar, FormControl, InputLabel, FormHelperText, InputAdornment, TextField, Button, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { countryManagementCreateOrUpdate, countryManagementMetaData, countryManagementGetById } from '../countryManagement.service';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Icon } from "@iconify/react";
import Loader from '../../../components/loader';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function CountryManagementCreation() {
  const location = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const type = queryParams.get("type");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState("");
  const [region, setRegion] = useState('');
  const [currency, setCurrency] = useState('');
  const [timezone, setTimezone] = useState('');
  const [countryData, setCountryData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [solutionData, setSolutionData] = useState([]);
  const [timezoneData, setTimezoneData] = useState([]);
  const [disableField, setDisableField] = useState("");

  const ALERT_DURATION = 2000;
  const alertClose = () => {
    setAlert('')
  }
  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
    fetchData()
    countriesMetaData()
  }, [type, editMode]);

  const coruntrySchema = z.object({
    countryName: z.string().min(1, { message: "Country Name  is required" }),
    regionName: z.string().min(1, { message: "Region is required" }),
    currency: z.string().min(1, { message: "Currency is required" }),
    countryCode: z.string().min(1, { message: "Country Code is required" }),
    timezone: z.string().min(1, { message: "Timezone is required" }),
    solutionIds: z.array(z.number()).min(1, { message: "Available Solutions is required" }),
    termsAndConditions: z.union([z.instanceof(File), z.string()]).optional().nullable(),
    privacyPolicy: z.union([z.instanceof(File), z.string()]).optional().nullable(),
  });

  const fetchData = async () => {
    if (editMode === true) {
      const getItem = countryManagementGetById(id).then(async (res) => {
        let countryListData = res.data
        setDisableField(countryListData?.countryLinkage)
        setValue('countryName', countryListData?.countryName);
        setValue('regionName', countryListData?.regionName);
        setValue('currency', countryListData?.currency);
        setValue('countryCode', countryListData?.countryCode);
        setValue('timezone', countryListData?.timezone);
        setValue('solutionIds', countryListData?.countrySolution?.map((u) => u.id) || []);
        setValue('termsAndConditions', countryListData?.termsAndConditions);
        setValue('privacyPolicy', countryListData?.privacyPolicy);

      })
    }
  };

  const { handleSubmit, formState: { errors }, control, setValue } = useForm({ resolver: zodResolver(coruntrySchema), mode: "onChange" });

  const countriesMetaData = async () => {
    await countryManagementMetaData(id, editMode).then((res) => {
      if (res.statusCode === 200) {
        setCountryData(res.data.countryList);
        setRegionData(res.data.countryList);
        setCurrencyData(res.data.countryList);
        setTimezoneData(res.data.countryList);
        setSolutionData(res.data.solutionManagementList);
      }
    })
  }

  const handleCountryChange = (selectedCountryName, fieldOnChange) => {
    fieldOnChange(selectedCountryName);

    const selectedCountry = countryData.find(
      (item) => item.name === selectedCountryName
    );

    if (selectedCountry) {
      setValue("countryName", selectedCountry.name, { shouldValidate: true });
      setValue("regionName", selectedCountry.regionName || "", { shouldValidate: true });
      setValue("currency", selectedCountry.currencyCode || "", { shouldValidate: true });
      setValue("countryCode", selectedCountry.isoCode || "", { shouldValidate: true });
      setValue("timezone", selectedCountry.timezone || "", { shouldValidate: true });
      setValue("countryId", selectedCountry.id, { shouldValidate: false });
    }
  };

  const handleRegionChange = (event) => {
    setRegion(event.target.value);
  };
  const handleCurrencyChange = (event) => {
    setCurrency(event.target.value);
  };
  const handleTimeZoneChange = (event) => {
    setTimezone(event.target.value);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      const selectedCountry = countryData.find((item) => item.name === data.countryName);

      const formData = new FormData();

      formData.append("countryName", data.countryName);
      formData.append("regionName", data.regionName);
      formData.append("currency", data.currency);
      formData.append("countryCode", data.countryCode);
      formData.append("timezone", data.timezone);
      formData.append("countryId", selectedCountry?.id || "");

      data.solutionIds.forEach((id, index) => {
        formData.append(`solutionIds[${index}]`, id);
      });

      if (data.termsAndConditions instanceof File) {
        formData.append("termsAndConditions", data.termsAndConditions);
      }

      if (data.privacyPolicy instanceof File) {
        formData.append("privacyPolicy", data.privacyPolicy);
      }

      countryManagementCreateOrUpdate(formData, editMode, id).then((res) => {
        if (res.statusCode === 201 || res.statusCode === 200) {
          setResponseMsg(res.message);
          setAlert('true');
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            editMode
              ? navigate(`/countryManagementView/${id}?type=edit`)
              : navigate('/countryManagement');
            alertClose();
          }, ALERT_DURATION);
        } else if (res.statusCode === 404) {
          setResponseMsg(res.message);
          setAlert('false');
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            alertClose();
          }, ALERT_DURATION);
        } else if (res.statusCode === 500 || res.statusCode === 400 || res.statusCode === 429) {
          setResponseMsg(res.message);
          setAlert('false');
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            alertClose();
          }, ALERT_DURATION);
        }
        else {
          setResponseMsg(res.message);
          setAlert('false');
          setLoading(false)
          setOpenAlert(true)
          setTimeout(() => {
            alertClose()
          }, ALERT_DURATION);
        }
      })

    } catch (error) {
      console.error(error);
    }
  };

  const handlePdfUpload = (e, onChange) => {
    const file = e.target.files?.[0];
    const size = 5 * 1024 * 1024;

    if (file) {
      if (file.type !== "application/pdf") {
        onChange(null);
      } else if (file.size > size) {
        onChange(null);
        setResponseMsg("File size should not exceed 5MB");
      } else {
        onChange(file);
        return;
      }
      setAlert('false');
      setOpenAlert(true);
      setLoading(false);
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
      e.target.value = "";
    }
  };

  const getFileName = (fileOrPath) => {
    if (!fileOrPath) return "";
    if (fileOrPath instanceof File) return fileOrPath.name;
    if (typeof fileOrPath === "string") return fileOrPath.split("/").pop();
    return "";
  };


  return (
    <>
      <Grid container alignItems={'center'}>
        <Grid size={{ xs: 11, sm: 11, md: 11, lg: 11, xl: 11 }}>
          <Typography className='headingAlign'> {editMode ? "Update New Country" : "Add New Country"}</Typography>
        </Grid>
        <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }} textAlign={'end'}>
          <Icon icon="ei:arrow-left"
            onClick={() => {
              editMode
                ? navigate(`/countryManagementView/${id}?type=edit`)
                : navigate('/countryManagement');
            }}
            width="30" height="30"
            style={{ color: '#285D54', cursor: 'pointer' }}
          />
        </Grid>

      </Grid>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container columnGap={6} mt={2}>
          <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
            <Typography mb={1} style={{ fontWeight: '700' }}>Country Name <span style={{ color: 'var(--red-color)' }}>*</span> </Typography>
            <Grid container>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth error={!!errors.countryName}>
                  <InputLabel id="country-select-label">Select the country name</InputLabel>
                  <Controller name="countryName" control={control} defaultValue=""
                    render={({ field }) => (
                      <Select labelId="country-select-label" id="country-select" disabled={disableField} label="Select the country name" value={field.value} onChange={(event) =>
                        handleCountryChange(event.target.value, field.onChange)
                      }
                      >
                        <MenuItem value="" disabled>Select the country name</MenuItem>
                        {countryData.map((data) => (
                          <MenuItem key={data.id} value={data.name}>
                            {data.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {!!errors.countryName && (
                    <FormHelperText error>{errors.countryName.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <Grid container mt={2}>
              <Typography mb={1} style={{ fontWeight: '700' }}>Region <span style={{ color: 'var(--red-color)' }}>*</span> </Typography>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth error={!!errors.regionName}>
                  <InputLabel id="country-select-label">Select region</InputLabel>
                  <Controller name="regionName" control={control} defaultValue={''}
                    render={({ field, fieldState: { error } }) => (
                      <Select labelId="country-select-label" id="country-select" label='Select region' disabled={disableField} value={region} onChange={handleRegionChange} {...field} error={!!error} helperText={error ? error.message : ''}>
                        <MenuItem value="" disabled>Select region</MenuItem>
                        {regionData.map((data) => (
                          <MenuItem key={data.regionName} value={data.regionName}>
                            {data.regionName}
                          </MenuItem>
                        ))}
                      </Select>)} />
                  {!!errors.regionName && (<FormHelperText error>{errors.regionName.message}</FormHelperText>)}
                </FormControl>
              </Grid>
            </Grid>

            <Grid container mt={2}>
              <Typography mb={1} style={{ fontWeight: '700' }}>Currency <span style={{ color: 'var(--red-color)' }}>*</span> </Typography>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth error={!!errors.currency}>
                  <InputLabel id="currency-select-label">Select currency</InputLabel>
                  <Controller name="currency" control={control} defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <Select labelId="currency-select-label" id="currency-select" label='Select currency' value={currency} disabled={disableField} onChange={handleCurrencyChange} {...field} error={!!error} helperText={error ? error.message : ''}>
                        <MenuItem value="" disabled>Select currency</MenuItem>
                        {currencyData.map((data) => (
                          <MenuItem key={data.currencyCode} value={data.currencyCode}>
                            {data.currencyCode}  ({data.currencySymbol} )
                          </MenuItem>
                        ))}
                      </Select>)} />
                  {!!errors.currency && (<FormHelperText error>{errors.currency.message}</FormHelperText>)}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
            <Grid container>
              <Typography mb={1} style={{ fontWeight: '700' }}>Country Code (ISO) <span style={{ color: 'var(--red-color)' }}>*</span> </Typography>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth>
                  <Controller name="countryCode" control={control} defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} id="countryCode" label="e.g., IN, US, KE..." placeholder="e.g., IN, US, KE..." disabled={disableField} type="text" variant="outlined" error={!!error} helperText={error ? error.message : ''} inputProps={{
                        maxLength: 5
                      }}
                        onChange={(e) => { const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, ''); field.onChange(value) }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container mt={2}>
              <Typography mb={1} style={{ fontWeight: '700' }}>Timezone <span style={{ color: 'var(--red-color)' }}>*</span> </Typography>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth error={!!errors.timezone}>
                  <InputLabel id="currency-select-label">Select timezone</InputLabel>
                  <Controller name="timezone" control={control} defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <Select labelId="timezone-select-label" id="currency-select" label='Select timezone' value={timezone} disabled={disableField} onChange={handleTimeZoneChange} {...field} error={!!error} helperText={error ? error.message : ''}>
                        <MenuItem value="" disabled>Select timezone</MenuItem>
                        {timezoneData.map((data) => (
                          <MenuItem key={data.timezone} value={data.timezone}>
                            {data.timezone}
                          </MenuItem>
                        ))}
                      </Select>)} />
                  {!!errors.timezone && (<FormHelperText error>{errors.timezone.message}</FormHelperText>)}
                </FormControl>
              </Grid>
            </Grid>

            <Grid container mt={2}>
              <Typography mb={1} style={{ fontWeight: '700' }}>Available Solutions <span style={{ color: 'var(--red-color)' }}>*</span> </Typography>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth error={!!errors.solutionIds}>
                  <Controller name="solutionIds" control={control} defaultValue={[]}
                    render={({ field }) => (
                      <Select labelId="user-type-label" id="user-type-select" multiple  {...field} value={field.value || []}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                        }}
                        input={<OutlinedInput />}
                        renderValue={(selected) =>
                          selected
                            .map((id) => {
                              const user = solutionData.find((item) => item.id === id);
                              return user?.name || id;
                            })
                            .join(', ')
                        } MenuProps={MenuProps}>
                        {solutionData.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            <Checkbox checked={field.value.includes(item.id)} sx={{ padding: 0.3 }} />
                            <ListItemText>
                              <Typography sx={{ fontSize: 12 }}>{item.name}</Typography>
                            </ListItemText>
                          </MenuItem>
                        ))}
                        {solutionData.length === 0 && <MenuItem>No Options</MenuItem>}
                      </Select>
                    )} />
                  {!!errors.solutionIds && <FormHelperText>{errors.solutionIds.message}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container mb={2} gap={6} mt={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Grid container>
              <Typography mb={1} fontWeight="700">Terms and Conditions</Typography>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <Controller name="termsAndConditions" control={control} defaultValue={null}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <input type="file" id="termsAndConditions" accept="application/pdf" hidden onChange={(e) => handlePdfUpload(e, field.onChange)} />
                        <TextField value={getFileName(field.value)} label="Attach the file in PDF format" variant="outlined" placeholder="Attach the file in PDF format"
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <label htmlFor="termsAndConditions">
                                  <img src="/assets/Attachment.svg" alt="attachIcon" style={{ cursor: "pointer" }} />
                                </label>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Grid container>
              <Typography mb={1} fontWeight="700">Privacy Policy</Typography>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <Controller name="privacyPolicy" control={control} defaultValue={null} render={({ field, fieldState: { error } }) => (
                    <>
                      <input type="file" id="privacyPolicy" accept="application/pdf" hidden onChange={(e) => handlePdfUpload(e, field.onChange)} />
                      <TextField value={getFileName(field.value)} label="Attach the file in PDF format" variant="outlined" placeholder="Attach the file in PDF format"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <label htmlFor="privacyPolicy">
                                <img src="/assets/Attachment.svg" alt="attachIcon" style={{ cursor: "pointer" }} />
                              </label>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>


        <Grid container mb={2} mt={44} >
          <Grid size={10}>
            <Button variant="contained" className='resetbtn' onClick={() => { navigate('/countryManagement') }} style={{ display: editMode ? 'none' : 'block' }} >Cancel</Button>
          </Grid>
          <Grid size={2} textAlign={'end'}>
            <Button variant="contained" className='createBtn' type="submit">{editMode ? "Update Country" : "Create Country"}</Button>
          </Grid>
        </Grid>
      </form>

      {loading && <Loader />}

      <Grid item xs={4} textAlign={'end'}>
        {(alert === 'true') && (
          <Snackbar open={openAlert} autoHideDuration={6000} onClose={alertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#B6FFC3',
                py: 1.5,
                px: 2,
                borderRadius: '30px',
                boxShadow: 3,
                maxWidth: 400,
                mr: -5
              }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 20C9 20 8.25 19.1667 7.66667 18.5C7.33333 18.1667 7 17.75 6.83333 17.6667C6.58333 17.5833 6 17.5833 5.58333 17.5833C4.66667 17.6667 3.66667 17.6667 3 17C2.33333 16.3333 2.33333 15.25 2.41667 14.4167C2.41667 13.9167 2.5 13.4167 2.33333 13.1667C2.25 12.9167 1.83333 12.5833 1.5 12.3333C0.833333 11.75 0 11 0 10C0 9 0.833333 8.25 1.5 7.66667C1.83333 7.33333 2.25 7 2.33333 6.83333C2.41667 6.58333 2.41667 6 2.41667 5.58333C2.33333 4.66667 2.33333 3.66667 3 3C3.66667 2.33333 4.75 2.33333 5.58333 2.41667C6.08333 2.41667 6.58333 2.5 6.83333 2.33333C7.08333 2.25 7.41667 1.83333 7.66667 1.5C8.25 0.833333 9 0 10 0C11 0 11.75 0.833333 12.3333 1.5C12.6667 1.83333 13 2.25 13.1667 2.33333C13.4167 2.41667 14 2.41667 14.4167 2.41667C15.3333 2.33333 16.3333 2.33333 17 3C17.6667 3.66667 17.6667 4.75 17.5833 5.58333C17.5833 6.08333 17.5 6.58333 17.6667 6.83333C17.75 7.08333 18.1667 7.41667 18.5 7.66667C19.1667 8.25 20 9 20 10C20 11 19.1667 11.75 18.5 12.3333C18.1667 12.6667 17.75 13 17.6667 13.1667C17.5833 13.4167 17.5833 14 17.5833 14.4167C17.6667 15.3333 17.6667 16.3333 17 17C16.3333 17.6667 15.25 17.6667 14.4167 17.5833C13.9167 17.5833 13.4167 17.5 13.1667 17.6667C12.9167 17.75 12.5833 18.1667 12.3333 18.5C11.75 19.1667 11 20 10 20ZM6.25 15.9167C6.66667 15.9167 7.08333 16 7.5 16.0833C8.08333 16.3333 8.58333 16.8333 9 17.3333C9.33333 17.75 9.83333 18.25 10.0833 18.25C10.3333 18.25 10.8333 17.75 11.1667 17.3333C11.5833 16.8333 12.0833 16.3333 12.6667 16.0833C13.25 15.8333 14 15.8333 14.6667 15.9167C15.1667 15.9167 15.8333 16 16 15.8333C16.1667 15.6667 16.0833 15 16.0833 14.5C16 13.8333 16 13.0833 16.25 12.5C16.5 11.9167 17 11.4167 17.5 11C17.8333 10.75 18.3333 10.25 18.3333 10C18.3333 9.75 17.8333 9.25 17.4167 8.91667C16.9167 8.5 16.4167 8 16.1667 7.41667C15.9167 6.83333 15.9167 6.08333 16 5.41667C16 4.91667 16.0833 4.25 15.9167 4.08333C15.75 3.91667 15.0833 4 14.5833 4C13.9167 4.08333 13.1667 4.08333 12.5833 3.83333C12 3.58333 11.5 3.08333 11.0833 2.58333C10.75 2.16667 10.25 1.66667 10 1.66667C9.75 1.66667 9.25 2.16667 8.91667 2.58333C8.5 3.08333 8 3.58333 7.41667 3.83333C6.83333 4.08333 6.08333 4.08333 5.41667 4C4.91667 4 4.25 3.91667 4.08333 4.08333C3.91667 4.25 4 4.91667 4 5.41667C4.08333 6.08333 4.08333 6.83333 3.83333 7.41667C3.58333 8 3.08333 8.5 2.58333 8.91667C2.16667 9.25 1.66667 9.75 1.66667 10C1.66667 10.25 2.16667 10.75 2.58333 11.0833C3.08333 11.5 3.58333 12 3.83333 12.5833C4.08333 13.1667 4.08333 13.9167 4 14.5833C4 15.0833 3.91667 15.75 4.08333 15.9167C4.25 16.0833 4.91667 16 5.41667 16C5.66667 16 5.91667 15.9167 6.25 15.9167ZM9.16667 13.6667L6.08333 10.5833L7.25 9.41667L9.16667 11.3333L13.5833 6.91667L14.75 8.08333L9.16667 13.6667Z" fill="#00AD5E" />
              </svg> &nbsp;&nbsp;
              <Typography
                sx={{ color: '#004d40', fontWeight: 600, fontSize: '0.95rem', flexGrow: 1, }}>
                {responseMsg}
              </Typography>
            </Box>
          </Snackbar>
        )}

        {(alert === 'false') && (
          <Snackbar open={openAlert} autoHideDuration={6000} onClose={alertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#FDEDED',
                py: 1.5,
                px: 4,
                borderRadius: '30px',
                boxShadow: 3,
                maxWidth: 400,
                mr: -2
              }}
            >
              <Icon icon="solar:close-circle-linear" width="26" height="26" style={{ color: 'var(--red-color)' }} /> &nbsp;&nbsp;
              <Typography
                sx={{
                  color: '#DA342E',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  flexGrow: 1,
                }}>
                {responseMsg}
              </Typography>
            </Box>
          </Snackbar>
        )}
      </Grid>
    </>
  )
}

export default CountryManagementCreation
